import { Form } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { FormModal } from "@/shared/components/FormModal";
import { useCreateTaskSchedule, useUpdateTaskSchedule } from "../hooks/useTaskSchedules";
import { useTaskScheduleStore } from "../store/task-schedule.store";
import type { CreateTaskScheduleRequest, UpdateTaskScheduleRequest } from "../types";
import { TaskScheduleForm, type TaskScheduleFormValues } from "./TaskScheduleForm";

function buildCreateData(values: TaskScheduleFormValues): CreateTaskScheduleRequest {
	const recurrence = values.recurrence;
	return {
		title: values.title,
		organizationId: values.organizationId,
		brigadeId: values.brigadeId,
		taskTypeId: values.taskTypeId,
		targetType: values.targetType,
		locations:
			values.targetType === "LOCATION"
				? (values.locations ?? [])
						.filter((entry) => entry.geometry)
						.map((entry) => ({ type: entry.type, geometry: entry.geometry }) as never)
				: undefined,
		plantingIds: values.targetType === "PLANT" ? values.plantingIds : undefined,
		priority: values.priority ?? "MEDIUM",
		recurrence,
		intervalCount: values.intervalCount ?? 1,
		weekdays: recurrence === "WEEKLY" ? values.weekdays : undefined,
		dayOfMonth: recurrence === "MONTHLY" ? values.dayOfMonth : undefined,
		timeOfDay: values.timeOfDay ? values.timeOfDay.format("HH:mm") : "",
		startDate: values.startDate ? values.startDate.format("YYYY-MM-DD") : "",
		endDate: values.endDate ? values.endDate.format("YYYY-MM-DD") : undefined,
		description: values.description,
		isActive: values.isActive ?? true,
	};
}

function buildUpdateData(values: TaskScheduleFormValues): UpdateTaskScheduleRequest {
	const recurrence = values.recurrence;
	return {
		title: values.title,
		taskTypeId: values.taskTypeId,
		priority: values.priority ?? "MEDIUM",
		recurrence,
		intervalCount: values.intervalCount ?? 1,
		weekdays: recurrence === "WEEKLY" ? (values.weekdays ?? null) : null,
		dayOfMonth: recurrence === "MONTHLY" ? (values.dayOfMonth ?? null) : null,
		timeOfDay: values.timeOfDay ? values.timeOfDay.format("HH:mm") : undefined,
		endDate: values.endDate ? values.endDate.format("YYYY-MM-DD") : null,
		description: values.description,
		isActive: values.isActive ?? true,
	};
}

export function TaskScheduleFormModal() {
	const [form] = Form.useForm<TaskScheduleFormValues>();
	const { modalOpen, editingItem, closeModal } = useTaskScheduleStore();

	const { mutate: create, isPending: isCreating } = useCreateTaskSchedule();
	const { mutate: update, isPending: isUpdating } = useUpdateTaskSchedule();

	useEffect(() => {
		if (modalOpen) {
			if (editingItem) {
				form.setFieldsValue({
					title: editingItem.title,
					organizationId: editingItem.organizationId,
					brigadeId: editingItem.brigadeId,
					taskTypeId: editingItem.taskTypeId,
					targetType: editingItem.targetType,
					priority: editingItem.priority,
					recurrence: editingItem.recurrence,
					intervalCount: editingItem.intervalCount,
					weekdays: editingItem.weekdays ?? undefined,
					dayOfMonth: editingItem.dayOfMonth ?? undefined,
					timeOfDay: editingItem.timeOfDay ? dayjs(editingItem.timeOfDay, "HH:mm") : null,
					startDate: dayjs(editingItem.startDate),
					endDate: editingItem.endDate ? dayjs(editingItem.endDate) : null,
					description: editingItem.description,
					isActive: editingItem.isActive,
				});
			} else {
				form.resetFields();
			}
		}
	}, [modalOpen, editingItem, form]);

	const close = () => {
		form.resetFields();
		closeModal();
	};

	const handleSubmit = () => {
		form
			.validateFields()
			.then((values) => {
				if (editingItem) {
					update({ id: editingItem.id, data: buildUpdateData(values) }, { onSuccess: close });
				} else {
					create(buildCreateData(values), { onSuccess: close });
				}
			})
			.catch(() => {
				// Validation failed - Form shows errors inline
			});
	};

	return (
		<FormModal
			open={modalOpen}
			editing={!!editingItem}
			title="Yangi takrorlanish"
			editTitle="Takrorlanishni tahrirlash"
			onCancel={close}
			onOk={handleSubmit}
			confirmLoading={isCreating || isUpdating}
			width={760}
		>
			<Form
				form={form}
				layout="vertical"
				requiredMark={false}
				className="mt-4"
				initialValues={{
					priority: "MEDIUM",
					recurrence: "DAILY",
					intervalCount: 1,
					isActive: true,
				}}
			>
				<TaskScheduleForm form={form} isEdit={!!editingItem} />
			</Form>
		</FormModal>
	);
}
